import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { Alert } from '@/components/Alert';
import { AuthSplitLayout } from '@/components/AuthSplitLayout';
import { Button } from '@/components/Button';
import { TextField } from '@/components/Form';
import { getErrorMessage } from '@/lib/api';
import { safeCpRedirect } from '@/lib/routes';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const location = useLocation();
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ emailOrUsername?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const state = location.state as LocationState | null;
  const redirectTo = safeCpRedirect(state?.from);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const errors: { emailOrUsername?: string; password?: string } = {};
      if (!emailOrUsername.trim()) errors.emailOrUsername = 'Enter your email or username.';
      if (!password) errors.password = 'Enter your password.';
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;

      setFormError(null);
      setSubmitting(true);
      void login({ emailOrUsername: emailOrUsername.trim(), password })
        .catch((error: unknown) => {
          setFormError(getErrorMessage(error, 'Could not sign in. Please try again.'));
        })
        .finally(() => setSubmitting(false));
    },
    [emailOrUsername, login, password],
  );

  if (isAuthenticated) return <Navigate to={redirectTo} replace />;

  return (
    <AuthSplitLayout
      headline="Every shop, one console."
      blurb="Register tenants, licence their tills and keep them trading — from one place, without ever touching a shop's POS or interrupting a sale."
      points={[
        'Tenant registration and branch setup',
        'Licences issued, extended and revoked',
        'Subscription plans with live usage measured against them',
        'Over-limit alerts before a shop hits a wall',
        'Per-device detail, right down to the last sync',
        'A stolen till cut off the moment you hear about it',
        'An audit log of every action taken in the console',
      ]}
      title="Super admin console"
      subtitle="Sign in to manage shops, tills and licences."
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        {formError ? <Alert tone="error">{formError}</Alert> : null}

        <TextField
          id="emailOrUsername"
          label="Email or username"
          type="text"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          autoFocus
          value={emailOrUsername}
          onChange={(event) => setEmailOrUsername(event.target.value)}
          error={fieldErrors.emailOrUsername}
          disabled={submitting}
        />

        <TextField
          id="password"
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors.password}
          disabled={submitting}
        />

        <Button type="submit" variant="primary" className="w-full" loading={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthSplitLayout>
  );
}
